import { ConfigurationType } from '@server/parser';
import { RelatedListProperties } from '@server/documentation';
import { ReactNode } from 'react';
import { NameProperties } from './ParserTypes';

// Base interface with common properties shared by all configuration items
export interface ConfigurationItemBase {
  id: string;
  name: string;
  label: string;
  type: string;
  parentType: string;
  metadataId: string;
  objectApiNames: string[];

  description?: string;
  helpText?: string;
  referencedObjects?: string[];
  link?: string;
  updatedAt?: string;
  createdAt?: string;
  updatedBy?: string;
  createdBy?: string;
  isActive?: boolean;
  namespace?: string;

  _oldId?: string; // Client only, temporary fix for tags until BE is migrated to metadataId

  stagesNames?: string[];
  usedByRecordTypes?: string[];
  usedByLeadingFieldPicklist?: string[];

  match?: string; // For search result matches
}

export interface ConfigurationItemField extends ConfigurationItemBase {
  parentType: ConfigurationType.fields;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

export interface ConfigurationItemLayout extends ConfigurationItemBase {
  parentType: ConfigurationType.layouts;
  buttons?: NameProperties[];
  relatedLists?: RelatedListProperties[];
}

export interface ConfigurationItemFlow extends ConfigurationItemBase {
  parentType: ConfigurationType.flows | ConfigurationType.processBuilderFlows;
  flowType?: string;
  flowDescription?: string;
  processType?: string;
}

export interface ConfigurationItemValidationRule extends ConfigurationItemBase {
  parentType: ConfigurationType.validationRules;
  errorMessage?: string;
  formula?: string;
}

export interface ConfigurationItemApexTrigger extends ConfigurationItemBase {
  parentType: ConfigurationType.apexTriggers;
}

export interface ConfigurationItemApexClass extends ConfigurationItemBase {
  parentType: ConfigurationType.apexClasses;
}

export interface ConfigurationItemApprovalProcess extends ConfigurationItemBase {
  parentType: ConfigurationType.approvalProcesses;
}

export interface ConfigurationItemWorkflowRule extends ConfigurationItemBase {
  parentType: ConfigurationType.workflowRules;
}

export interface ConfigurationItemRecordType extends ConfigurationItemBase {
  parentType: ConfigurationType.recordTypes;
}

export interface ConfigurationItemReport extends ConfigurationItemBase {
  parentType: ConfigurationType.reports;
}

export interface ConfigurationItemEmailTemplate extends ConfigurationItemBase {
  parentType: ConfigurationType.emailTemplates;
}

export interface ConfigurationItemCpq extends ConfigurationItemBase {
  parentType: ConfigurationType.cpqData;
}

export interface ConfigurationItemRollup extends ConfigurationItemBase {
  parentType: ConfigurationType.rollups;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

interface ConfigurationItemBaseSweepElement extends ConfigurationItemBase {
  actions?: string[];
  trigger?: string;
  actionsToRender?: ReactNode[];
}

// Sweep element types
export interface ConfigurationItemSweepAutomation extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepAutomation;
}

export interface ConfigurationItemSweepAlert extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepAlert;
}

export interface ConfigurationItemSweepAssignment extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepAssignment;
}

export interface ConfigurationItemSweepScheduledAssignment extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepScheduledAssignment;
}

export interface ConfigurationItemSweepDedupe extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepDedupe;
}

export interface ConfigurationItemSweepMatching extends ConfigurationItemBaseSweepElement {
  parentType: ConfigurationType.sweepMatching;
}

// Generic fallback for unclassified or other types
export interface ConfigurationItemGeneric extends ConfigurationItemBase {
  parentType:
    | ConfigurationType.objects
    | ConfigurationType.relatedObjects
    | ConfigurationType.others //TODO flatten on BE
    | ConfigurationType.unclassifiedConfigurations //TODO flatten on BE
    | string; // others & unclassifiedConfigurations items can be of any type
}

export type ConfigurationItem =
  | ConfigurationItemField
  | ConfigurationItemLayout
  | ConfigurationItemFlow
  | ConfigurationItemValidationRule
  | ConfigurationItemApexTrigger
  | ConfigurationItemApexClass
  | ConfigurationItemApprovalProcess
  | ConfigurationItemWorkflowRule
  | ConfigurationItemRecordType
  | ConfigurationItemReport
  | ConfigurationItemEmailTemplate
  | ConfigurationItemCpq
  | ConfigurationItemRollup
  | ConfigurationItemSweepAutomation
  | ConfigurationItemSweepAlert
  | ConfigurationItemSweepAssignment
  | ConfigurationItemSweepScheduledAssignment
  | ConfigurationItemSweepDedupe
  | ConfigurationItemSweepMatching
  | ConfigurationItemGeneric;

// Type guard functions for runtime type narrowing
export function isFieldConfigurationItem(item: ConfigurationItem): item is ConfigurationItemField {
  return item.parentType === ConfigurationType.fields;
}

export function isFlowConfigurationItem(item: ConfigurationItem): item is ConfigurationItemFlow {
  return (
    item.parentType === ConfigurationType.flows ||
    item.parentType === ConfigurationType.processBuilderFlows
  );
}

export function isRollupConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemRollup {
  return item.parentType === ConfigurationType.rollups;
}

export function isSweepElementConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemBaseSweepElement {
  return (
    item.parentType === ConfigurationType.sweepAutomation ||
    item.parentType === ConfigurationType.sweepAlert ||
    item.parentType === ConfigurationType.sweepAssignment ||
    item.parentType === ConfigurationType.sweepScheduledAssignment ||
    item.parentType === ConfigurationType.sweepDedupe ||
    item.parentType === ConfigurationType.sweepMatching
  );
}

export function isValidationRuleConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemValidationRule {
  return item.parentType === ConfigurationType.validationRules;
}

// Type guard to check if item has field-like properties (Field or Rollup)
export function hasFieldProperties(
  item: ConfigurationItem,
): item is ConfigurationItemField | ConfigurationItemRollup {
  return isFieldConfigurationItem(item) || isRollupConfigurationItem(item);
}
